// ─────────────────────────────────────────────────────────────────────────
// PER-USER ACCESS - single source of truth for the dashboard.
//
// Access is no longer a fixed role. Each user carries an explicit permission
// set, chosen with checkboxes when you add/edit them:
//   { modules: ['/leads', '/calendar', ...], readOnly: bool, hideMoney: bool }
//
// `modules` = which pages they can open (grouped like the sidebar).
// `readOnly` = can view but never create/edit/delete (enforced at the API
//              client too - see lib/api.js).
// `hideMoney` = mask every rupee figure (revenue / expenses / ROAS).
//
// Presets just pre-fill the checkboxes; they are not a role.
//
// UI-level gating. Server-side enforcement is a tracked follow-up.
// ─────────────────────────────────────────────────────────────────────────

// The module catalogue, grouped exactly like the sidebar. The Add/Edit-user
// panel renders these as collapsible groups with checkboxes. '/dashboard' is
// always granted (everyone needs a landing page) so it's not listed here.
// Every store page's own permission. The key is the page's address, except
// Orders, whose address '/commerce' is also the legacy all-of-the-store grant.
export const STORE_PAGES = [
  { path: '/growth/guide', label: 'Where to look' },
  { path: '/growth/actions', label: "Today's Actions" },
  { path: '/growth/findings', label: 'What We Found' },
  { path: '/growth/customer-value', label: 'Customer Value' },
  { path: '/growth/buyers', label: 'Who Bought' },
  { path: '/growth/campaigns', label: 'Campaigns' },
  { path: '/growth/leaks', label: 'Where you lose money' },
  { path: '/growth/channel-roles', label: 'Channel Roles' },
  { path: '/growth/funnels', label: 'Funnels' },
  { path: '/growth/creatives', label: 'Creatives' },
  { path: '/studio', label: 'Signal catalogue' },
  { path: '/signal-health', label: 'Signal Health' },
  { path: '/number-check', label: 'Number check' },
  { path: '/value-report', label: 'What Cookiebees added' },
  { path: '/sale-season', label: 'Sale season' },
  { path: '/commerce/orders', label: 'Orders' },
  { path: '/commerce/customers', label: 'Customers' },
  { path: '/commerce/abandoned', label: 'Abandoned checkouts' },
  { path: '/commerce/recovery', label: 'Recovery' },
  { path: '/commerce/recommendations', label: 'Recommendations' },
  { path: '/commerce/costs', label: 'Costs' },
  { path: '/commerce/attribution', label: 'Attribution' },
  { path: '/commerce/insights', label: 'Analytics & Insights' },
  { path: '/commerce/segments', label: 'Customer segments' },
  { path: '/commerce/recovery/email', label: 'Store emails' },
];
export const STORE_PAGE_PATHS = STORE_PAGES.map((i) => i.path);

export const MODULE_GROUPS = [
  {
    key: 'sales', label: 'Sales',
    items: [
      { path: '/leads', label: 'Pipeline' },
      { path: '/deals', label: 'Deals' },
      { path: '/offerings', label: 'Products & services' },
      { path: '/contacts', label: 'Contacts' },
      { path: '/cold-captures', label: 'Cold Captures' },
      { path: '/commerce', label: 'Commerce (e-commerce)' },
      { path: '/follow-ups', label: 'Follow-ups' },
      { path: '/tasks', label: 'Tasks' },
      { path: '/calendar', label: 'Calendar' },
      { path: '/meetings', label: 'Meetings' },
      { path: '/calls', label: 'Calls' },
    ],
  },
  {
    key: 'measurement', label: 'Measurement',
    items: [
      { path: '/tracking/attribution', label: 'Lead attribution' },
      { path: '/tracking/conversions', label: 'Conversions' },
      { path: '/tracking/destinations', label: 'Integrations' },
      { path: '/tracking/email', label: 'Email analytics' },
      { path: '/tracking/stream', label: 'Event stream' },
      { path: '/tracking/site-scan', label: 'Site scan' },
      { path: '/tracking/health', label: 'Event health' },
      { path: '/server-container', label: 'Server GTM container' },
      { path: '/tracking/webhooks', label: 'Webhooks' },
      { path: '/tracking/links', label: 'Links' },
    ],
  },
  {
    key: 'messaging', label: 'Messaging',
    items: [
      // /whatsapp grants the inbox AND broadcasts (broadcasts rides this module).
      { path: '/whatsapp', label: 'WhatsApp (inbox + broadcasts)' },
    ],
  },
  {
    key: 'templates', label: 'Templates',
    items: [
      { path: '/automation/whatsapp-templates', label: 'WhatsApp templates' },
      { path: '/automation/whatsapp-flows', label: 'WhatsApp flows' },
      { path: '/automation/workflow-emails', label: 'Workflow emails' },
      { path: '/automation/email-templates', label: 'System emails' },
    ],
  },
  {
    key: 'automation', label: 'Automation',
    items: [
      { path: '/automations', label: 'Workflows' },
      { path: '/scoring', label: 'Lead scoring' },
      { path: '/forms', label: 'Forms' },
      { path: '/popups', label: 'Popups' },
      { path: '/chat', label: 'Website chat' },
      { path: '/automation/sequences', label: 'Sequences' },
    ],
  },
  {
    key: 'academy', label: 'Academy',
    items: [
      { path: '/students', label: 'Students' },
      { path: '/cohorts', label: 'Cohorts' },
      { path: '/finance', label: 'Finance' },
      { path: '/expenses', label: 'Expenses' },
    ],
  },
  {
    // A store's pages, each its own permission (6 Oct 2026). They all rode
    // '/commerce' before, so a store could only grant everything or nothing:
    // a partner (Appbrew) could not be given Orders without Costs and every
    // report. '/commerce' is kept as a legacy grant that expands to all of
    // these (expandLegacy), so nobody who had it loses a page.
    key: 'store', label: 'Store', storeOnly: true,
    items: STORE_PAGES,
  },
  {
    key: 'workspace', label: 'Workspace',
    items: [
      // The guided setup (pages/setup).
      { path: '/setup', label: 'Setup' },
      { path: '/team', label: 'Team' },
      { path: '/settings', label: 'Settings' },
    ],
  },
];

export const ALWAYS_GRANTED = ['/dashboard'];

export const ALL_MODULE_PATHS = MODULE_GROUPS.flatMap((g) => g.items.map((i) => i.path));

// Convenience subsets used by presets.
// A rep needs the shared OPERATIONAL tools too (templates, workflows, WhatsApp
// flows, sequences) - these are workspace-wide config, not lead data, so they're
// safe to grant while the department DATA scope still limits which leads/inbox
// the rep sees. Without them a department rep is read-only ("can only see data").
const SALES_PATHS = [
  '/leads', '/contacts', '/calendar', '/meetings', '/calls', '/follow-ups', '/tasks', '/whatsapp',
  '/automations', '/automation/sequences',
  '/automation/whatsapp-templates', '/automation/whatsapp-flows', '/automation/email-templates',
];
const VIEWER_PATHS = [
  '/leads', '/contacts', '/calendar', '/meetings', '/follow-ups',
  '/tracking/email', '/tracking/destinations', '/tracking/webhooks', '/tracking/attribution', '/tracking/health',
  '/automations', '/scoring', '/forms', '/automation/workflow-emails', '/automation/email-templates', '/automation/whatsapp-templates',
];

// Quick-start presets. Pick one, then tweak the boxes.
export const PRESETS = {
  full:   { label: 'Full access',   blurb: 'Everything, including team + settings.',                         modules: [...ALL_MODULE_PATHS], readOnly: false, hideMoney: false },
  sales:  { label: 'Sales rep',     blurb: 'Pipeline, calendar, meetings and WhatsApp. No money or reports.', modules: [...SALES_PATHS],      readOnly: false, hideMoney: true  },
  viewer: { label: 'Agency viewer', blurb: 'Read-only. Leads + reports, no finances/cohorts, money hidden.',      modules: [...VIEWER_PATHS],     readOnly: true,  hideMoney: true  },
  blank:  { label: 'Blank',         blurb: 'Nothing checked - choose each module by hand.',                   modules: [],                    readOnly: false, hideMoney: false },
};

// The same quick-starts for a store workspace, built from its own pages.
const STORE_TEAM = ['/commerce/orders', '/commerce/customers', '/commerce/abandoned', '/commerce/recovery',
  '/commerce/recommendations', '/whatsapp', '/automations', '/automation/whatsapp-templates',
  '/automation/whatsapp-flows', '/commerce/recovery/email', '/calls'];
const STORE_VIEWER = STORE_PAGE_PATHS.filter((p) => p !== '/commerce/costs')
  .concat(['/tracking/stream', '/tracking/health', '/tracking/destinations']);
export const STORE_PRESETS = {
  full:   { label: 'Full access',   blurb: 'Everything, including team + settings.',                              modules: [...ALL_MODULE_PATHS], readOnly: false, hideMoney: false },
  sales:  { label: 'Store team',    blurb: 'Orders, customers, recovery and WhatsApp. Money hidden.',             modules: [...STORE_TEAM],       readOnly: false, hideMoney: true  },
  viewer: { label: 'Agency viewer', blurb: 'Read-only. Every report except Costs, money hidden.',                modules: [...STORE_VIEWER],     readOnly: true,  hideMoney: true  },
  blank:  { label: 'Blank',         blurb: 'Nothing checked - choose each page by hand.',                         modules: [],                    readOnly: false, hideMoney: false },
};

// ── Legacy role matrix (fallback only) ────────────────────────────────────
// Used to derive permissions for any user whose `permissions` JSON is still
// null (e.g. created before this feature). Once a user is saved through the new
// panel they carry an explicit `permissions` object and this is bypassed.
export const ROLES = {
  admin:   { label: 'Admin' },
  manager: { label: 'Manager' },
  staff:   { label: 'Staff' },
  analyst: { label: 'Analyst (read-only)' },
};
const ROLE_FALLBACK = {
  admin:   { modules: [...ALL_MODULE_PATHS], readOnly: false, hideMoney: false },
  manager: { modules: ALL_MODULE_PATHS.filter((p) => !['/team', '/settings'].includes(p)), readOnly: false, hideMoney: false },
  staff:   { modules: [...SALES_PATHS], readOnly: false, hideMoney: true },
  analyst: { modules: [...VIEWER_PATHS], readOnly: true, hideMoney: true },
};

function permsFromRole(role) {
  return ROLE_FALLBACK[role] || ROLE_FALLBACK.admin;
}

// ── Reading the current user's permissions ────────────────────────────────
// Stored as JSON in localStorage on login / from /me. Falls back to the legacy
// role matrix so nobody is locked out before the backfill runs.
export function getPermissions() {
  try {
    const raw = localStorage.getItem('userPermissions');
    if (raw) {
      const p = JSON.parse(raw);
      if (p && Array.isArray(p.modules)) {
        // Admins (anyone with Settings access) implicitly get EVERY module -
        // including ones added to the app after their permissions were saved -
        // so newly-shipped pages always appear without re-granting access.
        const modules = p.modules.includes('/settings') ? [...ALL_MODULE_PATHS] : p.modules;
        return { modules: withMerged(modules), readOnly: !!p.readOnly, hideMoney: !!p.hideMoney };
      }
    }
  } catch {}
  // Legacy fallback: derive from role string.
  return permsFromRole(getUserRole());
}

// A full workspace admin: has the Settings module (implicitly all modules) or is
// a legacy account with no permissions record. Mirrors the API's requireWorkspaceAdmin.
// Used to hide admin-only management (e.g. Departments) from restricted users.
export function isWorkspaceAdmin() {
  try {
    const raw = localStorage.getItem('userPermissions');
    if (raw) {
      const p = JSON.parse(raw);
      if (p && Array.isArray(p.modules)) return p.modules.includes('/settings');
    }
  } catch {}
  return true; // legacy / unrestricted account
}

export function getUserRole() {
  try {
    return localStorage.getItem('userRole') || '';
  } catch {
    return '';
  }
}

// Retired pages whose grant carries over to the page that replaced them.
// Acquisition and Analytics were merged into Lead attribution (24 Sep 2026);
// a user saved with either keeps the report they had rather than losing it.
const MERGED_INTO = { '/tracking/acquisition': '/tracking/attribution', '/analytics': '/tracking/attribution' };
// The legacy store grant: '/commerce' opened every store page, so it still does.
export function expandLegacy(modules) {
  const list = Array.isArray(modules) ? modules : [];
  if (!list.includes('/commerce')) return list;
  return [...new Set([...list.filter((m) => m !== '/commerce'), ...STORE_PAGE_PATHS])];
}

function withMerged(modules) {
  const out = new Set(expandLegacy(modules));
  for (const m of modules) if (MERGED_INTO[m]) out.add(MERGED_INTO[m]);
  return [...out];
}

// ── Helpers consumed across the app ───────────────────────────────────────
export function allowedPaths() {
  const p = getPermissions();
  return new Set([...ALWAYS_GRANTED, ...p.modules]);
}

export function canAccessPath(path) {
  if (ALWAYS_GRANTED.includes(path)) return true;
  return getPermissions().modules.includes(path);
}

export function isReadOnly() {
  return getPermissions().readOnly === true;
}

export function hideMoney() {
  return getPermissions().hideMoney === true;
}

// Where to land this user: dashboard if they have it, else their first module.
export function roleHome() {
  const allow = allowedPaths();
  if (allow.has('/dashboard')) return '/dashboard';
  const p = getPermissions();
  return p.modules[0] || '/dashboard';
}

// Legacy: a few callers still ask "is this the agency role". True when the
// user is read-only AND money is hidden (the agency-viewer shape).
export function isAnalyst() {
  const p = getPermissions();
  return p.readOnly && p.hideMoney;
}

// Back-compat for lib/auth.js re-exports.
export function accessForRole(role) {
  return { paths: permsFromRole(role).modules, ...permsFromRole(role), home: '/dashboard' };
}

// Short human summary for the team list, e.g. "Full access" / "8 modules".
export function summarizePermissions(perm) {
  const p = perm && Array.isArray(perm.modules)
    ? { modules: perm.modules, readOnly: !!perm.readOnly, hideMoney: !!perm.hideMoney }
    : null;
  if (!p) return { label: 'Legacy role', flags: [] };
  const flags = [];
  if (p.readOnly) flags.push('Read-only');
  if (p.hideMoney) flags.push('money hidden');
  const isFull = ALL_MODULE_PATHS.every((m) => p.modules.includes(m));
  const label = isFull ? 'Full access' : `${p.modules.length} module${p.modules.length === 1 ? '' : 's'}`;
  return { label, flags };
}
